import React from 'react'
import {Button, useBreakpointValue} from '@chakra-ui/react'
import {Link as RouterLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {
    getGDIHideStyles,
    mergeGDISx,
    pickGDIDomProps,
    resolveGDIStyles
} from '../../utils/gdi-styles'
import {useGetLinkData} from '../../../../../contexts/category-url-context'
import {resolveGDILink} from '../../utils/gdi-link-resolver'
import {useIsInsideGDILink} from '../../utils/gdi-link-context'
import {getExpandedFromContentArea} from '../../utils/gdi-content-area'
import {parseShortcodes} from '../../utils/gdi-shortcode-parser'
import {useBrand} from '../../../../../contexts/brand-context'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    BUTTON_LAYOUT_DEFAULTS,
    CTA_STYLE_MAP,
    FOCUS_VISIBLE,
    buildCtaStyleProps,
    extraStylePropsFor,
    getCtaInteractionProps,
    getCtaStyleNameByBreakpoint,
    getCtaStylesForResolve,
    getCtaTextByBreakpoint,
    getCtaVariant,
    getParentTileLinkHoverSx,
    resolveFontStyleProps,
    resolveResponsiveFontStyleProps
} from '../../utils/gdi-cta-style'
import ProductsPopover from './products-popover'
import GDICustomDrawer from '../gdiDrawerBlock'

/* Mega Menu Link helper: split shortcode-rendered HTML around badge spans
 * (inline-block <span> elements emitted by the gdi-shortcode-parser when an
 * author uses `{highlight: ...}` / `{backgroundColor: ...}` — those always
 * carry an inline `background-color:` declaration). Each text segment is
 * wrapped in a `<span data-mml-text>` so the animated underline (`::before`
 * in getMegaMenuLinkSx) can attach to text-only hosts; badges are emitted
 * untouched except for a `margin-left: 0.25em` that replaces any authored
 * whitespace separating them from preceding text (preserves the visual gap
 * without leaving a text node that would extend the underline up to the
 * badge edge).
 *
 * Best-effort regex: the parser's badge span content is normally plain
 * escaped text but may include a single icon SVG when `{icon, highlight, …}`
 * is used. `[\s\S]*?` handles both. Nested badges-within-badges are not a
 * supported shortcode pattern, so the non-greedy match is safe. */
const wrapMegaMenuTextRuns = (html) => {
    const BADGE_RE = /(\s*)<span style="([^"]*background-color[^"]*)"([^>]*)>([\s\S]*?)<\/span>/g
    let result = ''
    let lastIdx = 0
    let m
    while ((m = BADGE_RE.exec(html)) !== null) {
        const textBefore = html.slice(lastIdx, m.index)
        if (textBefore) result += `<span data-mml-text="">${textBefore}</span>`
        const styleAttr = `${m[2]};margin-left:0.25em`
        result += `<span style="${styleAttr}"${m[3]}>${m[4]}</span>`
        lastIdx = m.index + m[0].length
    }
    const tail = html.slice(lastIdx)
    if (tail) result += `<span data-mml-text="">${tail}</span>`
    /* No badge matched — wrap the whole HTML so the underline has a host. */
    return lastIdx === 0 ? `<span data-mml-text="">${html}</span>` : result
}

const GDICtaBlock = ({
    HideBlockOn,
    GDICtaBlockLink,
    GDICtaBlockStylesPerViewType,
    ...props
}) => {
    const hideStyles = getGDIHideStyles(HideBlockOn)
    if (hideStyles.doNotLoadComponent) return null

    const getLinkData = useGetLinkData()
    const isInsideLink = useIsInsideGDILink()
    // GDICtaBlockLink is a ContentArea array — extract the first expanded GDILinkBlock
    const linkBlock = getExpandedFromContentArea(GDICtaBlockLink)
    /* When an ancestor (e.g. GDIContentTileBlock) already wraps this subtree in a
     * link, render as a plain button to avoid nested `<a>` elements. The CTA's own
     * link is dropped in favor of the ancestor's. */
    const linkData = isInsideLink ? null : resolveGDILink(linkBlock, getLinkData)

    /* When the linked GDILinkBlock has LinkType 'Popover Product IDs' (comma-separated
     * LinkReference), the CTA opens a popover (desktop) or drawer (mobile) listing
     * the individual products instead of navigating to a combined product set URL. */
    const multiProductIds =
        !isInsideLink && linkBlock?.LinkType === 'Popover Product IDs' && typeof linkBlock?.LinkReference === 'string'
            ? linkBlock.LinkReference.split(',')
                  .map((id) => id.trim())
                  .filter(Boolean)
            : []
    const isMultiProductCta = multiProductIds.length > 0

    const {desktopHoverEntry, stylesForResolve} = getCtaStylesForResolve(
        GDICtaBlockStylesPerViewType
    )
    const {styles: responsiveStyles, hoverStyles} = resolveGDIStyles(stylesForResolve, CTA_STYLE_MAP)

    const {styles: fontStyleStyles, hoverStyles: fontStyleHoverStyles} = resolveGDIStyles(
        stylesForResolve,
        {FontStyle: 'fontStyle'}
    )
    Object.assign(responsiveStyles, resolveResponsiveFontStyleProps(fontStyleStyles.fontStyle))
    /* `FontStyle: 'unset'` on the base entry is the authored "clear everything
     * font-related" switch. The base resolution above already injects the
     * clearing props into `responsiveStyles` (and from there into the lg :hover
     * scope via `buildCtaStyleProps`), but we still need to (1) override the
     * per-hover FontStyle merged into `hoverStyles` and (2) strip the desktop
     * hover entry's FontStyle so the lg :hover layer doesn't re-introduce
     * weight / decoration / transform / italics on top. */
    const baseFontStyleValue =
        fontStyleStyles.fontStyle && typeof fontStyleStyles.fontStyle === 'object'
            ? fontStyleStyles.fontStyle.base
            : fontStyleStyles.fontStyle
    /* Treat any FontStyle whose token list INCLUDES `unset` as a clear-everything
     * directive — so authors can layer additional modifiers on top
     * (e.g. `unset medium italic`) and still get the hover styles wiped, with
     * the trailing modifiers re-applied via the regular base merge path. */
    const isBaseFontStyleUnset =
        typeof baseFontStyleValue === 'string' &&
        baseFontStyleValue
            .trim()
            .toLowerCase()
            .split(/[\s,]+/)
            .includes('unset')
    if (isBaseFontStyleUnset) {
        /* Pass the full base value so multi-token forms like `unset medium`
         * still apply the trailing modifiers on hover (medium weight) on top
         * of the unset clearing — the per-hover FontStyle authored on the
         * desktop hover entry is intentionally discarded below. */
        Object.assign(hoverStyles, resolveFontStyleProps(baseFontStyleValue))
    } else {
        Object.assign(hoverStyles, resolveFontStyleProps(fontStyleHoverStyles.fontStyle))
    }
    const ctaTextByBreakpoint = getCtaTextByBreakpoint(GDICtaBlockStylesPerViewType)
    const styleNameByBreakpoint = getCtaStyleNameByBreakpoint(GDICtaBlockStylesPerViewType)
    const ctaText = useBreakpointValue(ctaTextByBreakpoint) ?? ctaTextByBreakpoint.base
    const styleName = useBreakpointValue(styleNameByBreakpoint) ?? styleNameByBreakpoint.base

    const sanitizedDesktopHoverEntry =
        isBaseFontStyleUnset && desktopHoverEntry
            ? {...desktopHoverEntry, FontStyle: undefined}
            : desktopHoverEntry

    const {directStyles, sxStyles} = buildCtaStyleProps(
        responsiveStyles,
        sanitizedDesktopHoverEntry,
        styleName
    )
    const variant = getCtaVariant(styleName)
    const extraStyleProps = extraStylePropsFor(styleName)
    const {hoverProp, extraSpread} = getCtaInteractionProps(extraStyleProps, hoverStyles)
    const buttonText = ctaText || linkBlock?.AriaLabel || linkBlock?.Name || ''
    /* Inline CMS directives in CtaText — same shortcode syntax used by GDITextBlock,
     * e.g. `Shop {fontColor: '#fff', text: 'now'}` or `{brandName: true}`. We only
     * pay the parse cost (and use dangerouslySetInnerHTML) when the text actually
     * contains a shortcode opener; otherwise render as a plain string. Hydration-
     * dependent directives (tooltip, readMore) are not supported here — they
     * require effects and aren't meaningful inside a button label. */
    const {brandId} = useBrand()
    const {data: customer} = useCurrentCustomer()
    const isRegistered = customer?.isRegistered ?? false
    const brandName = brandId ? brandId.charAt(0).toUpperCase() + brandId.slice(1) : undefined
    const hasShortcode = typeof buttonText === 'string' && buttonText.includes('{')
    const rawButtonTextHtml = hasShortcode
        ? parseShortcodes(buttonText, {brandName, isRegistered})
        : null
    /* Mega Menu Link uses a `::before` line on `[data-mml-text]` wrappers
     * (see getMegaMenuLinkSx) so the scale-from-left underline animation can
     * cover text runs without crossing inline-block badges. Here we split the
     * shortcode-rendered HTML around any badge spans (the shortcode parser's
     * `{highlight: ...}` directive emits inline-block <span> elements with an
     * inline `background-color:` declaration) so that each text run lives in
     * its own wrapper and badges sit OUTSIDE any wrapper. Authored whitespace
     * adjacent to a badge is stripped and replaced with a transparent
     * `margin-left: 0.25em` on the badge — keeps the visual gap while leaving
     * no underline-eligible text between text and badge. */
    const buttonTextHtml =
        rawButtonTextHtml && styleName === 'Mega Menu Link'
            ? wrapMegaMenuTextRuns(rawButtonTextHtml)
            : rawButtonTextHtml
    /* Resolve any {fontSize: <token>} directives the parser tagged with
     * data-gdi-textstyle into actual Chakra textStyle rules (same trick used by
     * GDITextBlock). Without this, design-system tokens like `webH1` render
     * inert because the attribute alone applies no styles. */
    const textStyleRules = (() => {
        if (!buttonTextHtml) return null
        const rules = {}
        const re = /data-gdi-textstyle="([^"]+)"/g
        let m
        while ((m = re.exec(buttonTextHtml)) !== null) {
            rules[`& [data-gdi-textstyle="${m[1]}"]`] = {textStyle: m[1]}
        }
        return Object.keys(rules).length > 0 ? rules : null
    })()
    const buttonChildren = hasShortcode ? (
        <span dangerouslySetInnerHTML={{__html: buttonTextHtml}} />
    ) : styleName === 'Mega Menu Link' && buttonText ? (
        /* Plain-text Mega Menu Link path: wrap the label in a [data-mml-text]
         * span so the animated `::before` underline (scoped to that selector
         * in getMegaMenuLinkSx) has a host element. Without a wrapper the
         * line would have nowhere to attach for non-shortcode CTA labels. */
        <span data-mml-text="">{buttonText}</span>
    ) : (
        buttonText
    )
    const ariaLabel = linkBlock?.AriaLabel || undefined
    const buttonProps = {
        'aria-label': ariaLabel,
        'data-block-type': 'GDICtaBlock',
        variant,
        _hover: hoverProp,
        _focusVisible: FOCUS_VISIBLE,
        ...BUTTON_LAYOUT_DEFAULTS,
        ...extraSpread,
        ...directStyles,
        sx: mergeGDISx(
            sxStyles,
            hideStyles.sx,
            textStyleRules,
            getParentTileLinkHoverSx(styleName, hoverProp)
        ),
        ...pickGDIDomProps(props)
    }

    /* Multi-product CTA: render a plain button (no anchor) wrapped in the
     * popover/drawer trigger. Must be checked before the !linkData branch below
     * because resolveGDILink returns null for 'Popover Product IDs' (it is not
     * a navigable link), which would otherwise cause an early plain-button return. */
    if (isMultiProductCta) {
        const triggerButton = (
            <Button {...buttonProps}>
                {buttonChildren}
            </Button>
        )
        return <ProductsPopover productIds={multiProductIds} trigger={triggerButton} />
    }

    if (!linkData) {
        /* Custom Drawer: open a WebDrawer with CMS-authored GDIDrawerBlock content. */
        if (linkBlock?.LinkType === 'Custom Drawer' && !isInsideLink) {
            const customDrawerBlock = getExpandedFromContentArea(linkBlock?.CustomDrawer)
            if (customDrawerBlock) {
                return (
                    <GDICustomDrawer
                        drawerBlock={customDrawerBlock}
                        renderTrigger={({onOpen}) => (
                            <Button onClick={onOpen} {...buttonProps}>
                                {buttonChildren}
                            </Button>
                        )}
                    />
                )
            }
        }

        /* System Drawer ID: dispatch gdi:openDrawer so the relevant modal/drawer opens.
         * (LinkReference 'didomi' opens the Didomi cookie preferences UI directly.)
         * Supported values: login, register, mini-cart, search. */
        const drawerOnClick =
            linkBlock?.LinkType === 'System Drawer ID' && linkBlock?.LinkReference
                ? (e) => {
                      e.preventDefault()
                      if (typeof window !== 'undefined') {
                          window.dispatchEvent(
                              new CustomEvent('gdi:openDrawer', {
                                  detail: {drawer: linkBlock.LinkReference}
                              })
                          )
                      }
                  }
                : undefined

        return (
            <Button onClick={drawerOnClick} {...buttonProps}>
                {buttonChildren}
            </Button>
        )
    }

    const {href, isExternal, target, rel} = linkData

    if (isExternal) {
        /* Render as a plain <a> rather than Chakra's Link: the latter injects a
         * default `_hover: {textDecoration: 'underline'}` that overrides the CTA
         * style's own decoration (e.g. Outline), showing an unwanted underline on
         * hover. A bare anchor lets the CTA styles fully control the decoration,
         * matching the internal RouterLink path. */
        return (
            <Button as="a" href={href} target={target} rel={rel} {...buttonProps}>
                {buttonChildren}
            </Button>
        )
    }

    return (
        <Button as={RouterLink} to={href} {...buttonProps}>
            {buttonChildren}
        </Button>
    )
}

GDICtaBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    GDICtaBlockLink: PropTypes.array,
    GDICtaBlockStylesPerViewType: PropTypes.array,
    GDICtaBlockAudienceTargeting: PropTypes.object
}

export default GDICtaBlock
